import {
  PDFDocument,
  degrees,
  pdfjs,
  parsePageSelection,
  formatError,
  renderPdfPageToCanvas,
  PdfPageSelector,
  PdfResultViewer,
  getPdfDocumentParams
} from './pdf-engine.js';
import { byId, isAr, message, formatBytes, saveBlob, trackEvent, showFileAdvisory } from './extra-common.js';

// DOM Elements
const fileInput = byId('pdf-file-input');
const drop = byId('pdf-dropzone');
const controls = byId('pdf-controls');
const docTitle = byId('pdf-doc-title');
const fileSizeEl = byId('pdf-file-size');
const pageCountEl = byId('pdf-page-count');
const resetBtn = byId('pdf-files-reset');

const opSelect = byId('pdf-operation');
const actionCardBtns = Array.from(document.querySelectorAll('#pdf-toolkit-app .pdf-action-card'));
const rotationWrap = byId('pdf-rotation-wrap');
const rotationSelect = byId('pdf-rotation');
const rotBtns = Array.from(document.querySelectorAll('#pdf-toolkit-app .pdf-rot-btn'));

const mergeContainer = byId('pdf-merge-container');
const addMoreInput = byId('pdf-add-more');
const fileListEl = byId('pdf-file-list');

const pagesAccordion = byId('pdf-pages-accordion');
const pagesWrap = byId('pdf-pages-wrap');
const pagesGrid = byId('pdf-pages-grid');
const gridCount = byId('pdf-grid-count');
const rangeInput = byId('pdf-pages');
const rangeFrom = byId('pdf-range-from');
const rangeTo = byId('pdf-range-to');
const applyRangeBtn = byId('pdf-range-apply');
const summaryEl = byId('pdf-selection-summary');
const hintEl = byId('pdf-mode-hint');

const processBtn = byId('pdf-process-btn');
const processBtnText = byId('pdf-process-btn-text');

// State
let files = [];
let singlePdfDoc = null;
let singlePdfBytes = null;
let currentSinglePageCount = 0;
let pageSelector = null;
let resultViewer = null;
let currentAction = 'extract';
let readRequestId = 0;
let livePreviewTimer = null;

// Initialize Result Viewer
resultViewer = new PdfResultViewer({
  canvas: byId('pdf-preview-canvas'),
  prevBtn: byId('pdf-prev-page-btn'),
  nextBtn: byId('pdf-next-page-btn'),
  currentNumEl: byId('pdf-current-page-num'),
  totalPagesEl: byId('pdf-total-preview-pages'),
  statsEl: byId('pdf-preview-stats'),
  loadingEl: byId('pdf-preview-loading'),
  downloadBtn: null, // Unified through processBtn
  backBtn: null,
  isAr
});

// Initialize Page Selector
pageSelector = new PdfPageSelector({
  gridEl: pagesGrid,
  rangeInput,
  fromInput: rangeFrom,
  toInput: rangeTo,
  applyRangeBtn,
  summaryEl,
  hintEl,
  countEl: gridCount,
  presetBtns: Array.from(document.querySelectorAll('#pdf-toolkit-app [data-preset]')),
  isAr,
  onChange: () => {
    scheduleLivePreview(150);
  }
});

function scheduleLivePreview(delay = 180) {
  if (livePreviewTimer) clearTimeout(livePreviewTimer);
  livePreviewTimer = setTimeout(async () => {
    if (!files.length) return;
    if (currentAction === 'merge' && files.length < 2) return;
    try {
      const { bytes, filename } = await buildOutputPdf();
      await resultViewer.loadBytes(bytes, filename);
    } catch {
      // Keep existing preview if temporary state is empty
    }
  }, delay);
}

function updateOperationUI() {
  currentAction = opSelect?.value || 'extract';
  const isMerge = currentAction === 'merge';

  // Sync Action Card visual buttons
  actionCardBtns.forEach((btn) => {
    const isAct = btn.dataset.action === currentAction;
    btn.classList.toggle('active', isAct);
    btn.setAttribute('aria-checked', String(isAct));
  });

  if (isMerge) {
    if (mergeContainer) mergeContainer.hidden = false;
    if (pagesAccordion) pagesAccordion.hidden = true;
    if (rotationWrap) rotationWrap.hidden = true;
    if (processBtnText) {
      processBtnText.textContent = isAr ? 'دمج وتنزيل ملفات PDF' : 'Merge & Download PDFs';
    }
  } else {
    if (mergeContainer) mergeContainer.hidden = true;
    if (pagesAccordion) pagesAccordion.hidden = false;
    if (rotationWrap) rotationWrap.hidden = currentAction !== 'rotate';

    if (currentAction === 'rotate') {
      pageSelector.setMode('rotate');
      if (processBtnText) {
        processBtnText.textContent = isAr ? 'حفظ وتنزيل المستند بعد التدوير' : 'Save & Download Rotated PDF';
      }
    } else if (currentAction === 'extract') {
      pageSelector.setMode('extract');
      if (processBtnText) {
        processBtnText.textContent = isAr ? 'استخراج وتنزيل الصفحات المحددة' : 'Extract & Download Selected Pages';
      }
    } else if (currentAction === 'remove') {
      pageSelector.setMode('remove');
      if (processBtnText) {
        processBtnText.textContent = isAr ? 'حذف الصفحات وتنزيل المستند الناتج' : 'Delete Pages & Download Output PDF';
      }
    }
  }
}

// Action Cards Click Listeners
actionCardBtns.forEach((btn) => {
  btn.addEventListener('click', () => {
    const act = btn.dataset.action;
    if (opSelect) opSelect.value = act;
    updateOperationUI();
    scheduleLivePreview(100);
  });
});

if (opSelect) {
  opSelect.addEventListener('change', () => {
    updateOperationUI();
    scheduleLivePreview(100);
  });
}

// Rotation Buttons Click Listeners
rotBtns.forEach((btn) => {
  btn.addEventListener('click', () => {
    const angle = Number(btn.dataset.angle) || 90;
    rotBtns.forEach((b) => b.classList.toggle('active', b === btn));
    if (rotationSelect) rotationSelect.value = String(angle);
    pageSelector.setGlobalRotation(angle);
    scheduleLivePreview(120);
  });
});

rotationSelect?.addEventListener('change', () => {
  const angle = Number(rotationSelect.value) || 90;
  rotBtns.forEach((b) => b.classList.toggle('active', Number(b.dataset.angle) === angle));
  pageSelector.setGlobalRotation(angle);
  scheduleLivePreview(120);
});

async function renderMergeFilesList() {
  fileListEl.replaceChildren();
  let totalBytes = 0;
  let totalPages = 0;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    totalBytes += file.size;

    const card = document.createElement('div');
    card.className = 'pdf-merge-file-card';

    const left = document.createElement('div');
    left.className = 'pdf-merge-file-left';

    const canvas = document.createElement('canvas');
    canvas.className = 'pdf-merge-cover-canvas';
    left.appendChild(canvas);

    const info = document.createElement('div');
    info.className = 'pdf-merge-file-info';

    const name = document.createElement('span');
    name.className = 'pdf-merge-file-name';
    name.textContent = `${i + 1}. ${file.name}`;

    const meta = document.createElement('span');
    meta.className = 'pdf-merge-file-meta';
    meta.textContent = `${formatBytes(file.size)}`;

    info.append(name, meta);
    left.appendChild(info);

    const actions = document.createElement('div');
    actions.className = 'pdf-merge-actions';

    const upBtn = document.createElement('button');
    upBtn.type = 'button';
    upBtn.className = 'btn-secondary pdf-order-btn';
    upBtn.textContent = '↑';
    upBtn.title = isAr ? 'تحريك لأعلى' : 'Move up';
    upBtn.disabled = i === 0;
    upBtn.addEventListener('click', () => {
      [files[i], files[i - 1]] = [files[i - 1], files[i]];
      renderMergeFilesList();
      scheduleLivePreview(150);
    });

    const downBtn = document.createElement('button');
    downBtn.type = 'button';
    downBtn.className = 'btn-secondary pdf-order-btn';
    downBtn.textContent = '↓';
    downBtn.title = isAr ? 'تحريك لأسفل' : 'Move down';
    downBtn.disabled = i === files.length - 1;
    downBtn.addEventListener('click', () => {
      [files[i], files[i + 1]] = [files[i + 1], files[i]];
      renderMergeFilesList();
      scheduleLivePreview(150);
    });

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'btn-secondary pdf-remove-file-btn';
    removeBtn.innerHTML = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M18 6L6 18M6 6l12 12"/></svg>';
    removeBtn.title = isAr ? 'حذف الملف من القائمة' : 'Remove file from merge';
    removeBtn.addEventListener('click', () => {
      files.splice(i, 1);
      if (!files.length) {
        resetWorkspace();
      } else {
        renderMergeFilesList();
        scheduleLivePreview(150);
      }
    });

    actions.append(upBtn, downBtn, removeBtn);
    card.append(left, actions);
    fileListEl.appendChild(card);

    // Render cover in background
    (async () => {
      try {
        const doc = await pdfjs.getDocument(getPdfDocumentParams({ data: new Uint8Array(await file.arrayBuffer()) })).promise;
        totalPages += doc.numPages;
        meta.textContent = `${formatBytes(file.size)} · ${doc.numPages} ${isAr ? 'صفحة' : 'pages'}`;
        const page = await doc.getPage(1);
        await renderPdfPageToCanvas(page, canvas, 60, 0);
        await doc.destroy();
      } catch {
        // Fallback silently
      }
    })();
  }

  fileSizeEl.textContent = formatBytes(totalBytes);
  docTitle.textContent = isAr ? `${files.length} ملفات للدمج` : `${files.length} files to merge`;
  pageCountEl.textContent = isAr ? `${files.length} ملفات` : `${files.length} files`;
  if (files.length >= 2) scheduleLivePreview(200);

  if (files.length >= 15 || totalBytes >= 50 * 1024 * 1024) {
    showFileAdvisory(
      byId('pdf-toolkit-app'),
      isAr
        ? `مجموعة ملفات PDF كبيرة (${files.length} ملفات · ${formatBytes(totalBytes)}) — الدمج يتم محلياً في متصفحك.`
        : `Large PDF set (${files.length} files · ${formatBytes(totalBytes)}) — merging 100% locally on your device.`
    );
  } else {
    showFileAdvisory(byId('pdf-toolkit-app'), '', false);
  }
}

const dropDefault = byId('pdf-dropzone-default');
const dropLoading = byId('pdf-dropzone-loading');
const loadingFilename = byId('pdf-loading-filename');
const progressBar = byId('pdf-progress-bar');
const loadingStep = byId('pdf-loading-step');
const loadingPct = byId('pdf-loading-pct');

function setPdfDropzoneLoading(show, fileName = '', fileSize = 0) {
  if (dropDefault) dropDefault.style.display = show ? 'none' : 'flex';
  if (dropLoading) dropLoading.style.display = show ? 'flex' : 'none';
  if (show && loadingFilename) {
    loadingFilename.textContent = `${fileName} · ${formatBytes(fileSize)}`;
  }
}

function setPdfProgress(pct, statusText) {
  const val = Math.max(0, Math.min(100, Math.round(pct)));
  if (progressBar) progressBar.style.width = `${val}%`;
  if (loadingPct) loadingPct.textContent = `${val}%`;
  if (loadingStep && statusText) loadingStep.textContent = statusText;
}

function readFileWithProgress(file, onProgress) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) {
        const pct = Math.round((e.loaded / e.total) * 100);
        onProgress(pct, e.loaded, e.total);
      }
    };
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('File read failed'));
    reader.readAsArrayBuffer(file);
  });
}

async function loadSinglePdf(file) {
  setPdfDropzoneLoading(true, file.name, file.size);
  setPdfProgress(10, isAr ? 'جارٍ قراءة بيانات المستند من جهازك...' : 'Reading document bytes from storage...');

  const buffer = await readFileWithProgress(file, (pct, loaded, total) => {
    const scaled = Math.round(10 + pct * 0.45);
    const text = isAr 
      ? `جارٍ قراءة المستند (${formatBytes(loaded)} من ${formatBytes(total)})...`
      : `Reading document (${formatBytes(loaded)} of ${formatBytes(total)})...`;
    setPdfProgress(scaled, text);
  });

  setPdfProgress(60, isAr ? 'جارٍ تحليل هيكل PDF واستخراج الصفحات...' : 'Parsing PDF structure and pages...');
  singlePdfBytes = new Uint8Array(buffer);

  if (singlePdfDoc) {
    try { await singlePdfDoc.destroy(); } catch { /* ignore */ }
    singlePdfDoc = null;
  }

  singlePdfDoc = await pdfjs.getDocument(getPdfDocumentParams({ data: singlePdfBytes.slice(0) })).promise;
  currentSinglePageCount = singlePdfDoc.numPages;

  setPdfProgress(85, isAr ? `جارٍ تجهيز بطاقات ${currentSinglePageCount} صفحة...` : `Generating cards for ${currentSinglePageCount} pages...`);

  docTitle.textContent = file.name;
  fileSizeEl.textContent = formatBytes(file.size);
  pageCountEl.textContent = isAr ? `${currentSinglePageCount} صفحة` : `${currentSinglePageCount} pages`;

  pageSelector.mount(singlePdfDoc, currentSinglePageCount, currentAction);
  updateOperationUI();

  setPdfProgress(100, isAr ? 'جاهز!' : 'Ready!');
  await new Promise((r) => setTimeout(r, 160));
  setPdfDropzoneLoading(false);
  message('pdf-status', isAr ? `تم تجهيز ${currentSinglePageCount} صفحة. المعاينة المباشرة جاهزة بالأسفل.` : `Ready with ${currentSinglePageCount} pages. Live preview ready below.`, 'success');

  // Trigger live preview immediately
  scheduleLivePreview(60);
}

async function handleSelectedFiles(incomingList) {
  const chosen = Array.from(incomingList).filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
  if (!chosen.length) {
    message('pdf-status', isAr ? 'يرجى اختيار ملفات PDF صالحة.' : 'Please choose valid PDF files.', 'error');
    return;
  }

  readRequestId++;
  files = chosen;
  trackEvent('tool_started', 'pdf-toolkit');
  controls.hidden = false;
  drop.classList.add('has-files');

  if (files.length > 1) {
    if (opSelect) opSelect.value = 'merge';
    updateOperationUI();
    await renderMergeFilesList();
  } else {
    if (opSelect && opSelect.value === 'merge') opSelect.value = 'extract';
    await loadSinglePdf(files[0]);
  }
}

fileInput.addEventListener('change', () => handleSelectedFiles(fileInput.files));
if (addMoreInput) {
  const addMoreDropzone = addMoreInput.closest('.kz-mini-dropzone, .dropzone');
  const appendExtraFiles = (fileList) => {
    const extra = Array.from(fileList || []).filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (!extra.length) return;
    files = [...files, ...extra];
    renderMergeFilesList();
  };

  addMoreInput.addEventListener('change', () => {
    appendExtraFiles(addMoreInput.files);
    addMoreInput.value = '';
  });

  if (addMoreDropzone) {
    addMoreDropzone.addEventListener('dragover', (event) => {
      event.preventDefault();
      addMoreDropzone.classList.add('dragover');
    });
    addMoreDropzone.addEventListener('dragleave', () => addMoreDropzone.classList.remove('dragover'));
    addMoreDropzone.addEventListener('drop', (event) => {
      event.preventDefault();
      addMoreDropzone.classList.remove('dragover');
      if (event.dataTransfer && event.dataTransfer.files) {
        appendExtraFiles(event.dataTransfer.files);
      }
    });
    addMoreDropzone.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        addMoreInput.click();
      }
    });
  }
}

drop.addEventListener('click', (event) => {
  if (!event.target.closest('button') && !event.target.closest('input')) fileInput.click();
});
drop.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); fileInput.click(); }
});
drop.addEventListener('dragover', (event) => {
  event.preventDefault();
  drop.classList.add('dragover');
});
drop.addEventListener('dragleave', () => drop.classList.remove('dragover'));
drop.addEventListener('drop', (event) => {
  event.preventDefault();
  drop.classList.remove('dragover');
  handleSelectedFiles(event.dataTransfer.files);
});

function resetWorkspace() {
  readRequestId++;
  files = [];
  singlePdfBytes = null;
  fileInput.value = '';
  if (addMoreInput) addMoreInput.value = '';
  pageSelector.destroy();
  resultViewer.destroy();
  controls.hidden = true;
  drop.classList.remove('has-files');
  setPdfDropzoneLoading(false);
  message('pdf-status', '');
}

resetBtn.addEventListener('click', resetWorkspace);

async function buildOutputPdf() {
  if (!files.length) throw new Error(isAr ? 'لم يتم اختيار ملفات.' : 'No files selected.');

  if (currentAction === 'merge') {
    if (files.length < 2) throw new Error(isAr ? 'اختر ملفين على الأقل للدمج.' : 'Choose at least 2 files to merge.');
    const merged = await PDFDocument.create();
    let count = 0;
    for (const f of files) {
      const srcDoc = await PDFDocument.load(await f.arrayBuffer());
      const copied = await merged.copyPages(srcDoc, srcDoc.getPageIndices());
      copied.forEach((p) => merged.addPage(p));
      count += copied.length;
    }
    return { bytes: await merged.save(), filename: 'merged-document.pdf' };
  }

  // Single Document Action (extract, remove, rotate)
  if (!singlePdfBytes) throw new Error(isAr ? 'لم يتم تحميل المستند.' : 'Document not loaded.');
  const srcDoc = await PDFDocument.load(singlePdfBytes);
  const total = srcDoc.getPageCount();
  const selected = pageSelector.getSelectedIndices();

  if (currentAction === 'extract') {
    if (!selected.length) throw new Error(isAr ? 'حدد صفحة واحدة على الأقل للاستخراج.' : 'Select at least one page to extract.');
    const outDoc = await PDFDocument.create();
    const copied = await outDoc.copyPages(srcDoc, selected);
    copied.forEach((p) => outDoc.addPage(p));
    return { bytes: await outDoc.save(), filename: 'extracted-pages.pdf' };
  } else if (currentAction === 'remove') {
    if (selected.length >= total) throw new Error(isAr ? 'لا يمكن حذف جميع الصفحات. يجب إبقاء صفحة واحدة على الأقل.' : 'Cannot remove all pages.');
    if (!selected.length) throw new Error(isAr ? 'حدد الصفحات المراد حذفها بالنقر عليها.' : 'Select the pages you want to remove.');
    // Remove descending
    [...selected].sort((a, b) => b - a).forEach((idx) => srcDoc.removePage(idx));
    return { bytes: await srcDoc.save(), filename: 'pages-removed.pdf' };
  } else if (currentAction === 'rotate') {
    const rotations = pageSelector.rotations;
    const globalAngle = Number(rotationSelect?.value) || 0;
    for (let i = 0; i < total; i++) {
      const page = srcDoc.getPage(i);
      const cardAngle = rotations[i] || 0;
      const angleToAdd = selected.includes(i) ? (cardAngle || globalAngle || 90) : cardAngle;
      if (angleToAdd) {
        page.setRotation(degrees((page.getRotation().angle + angleToAdd) % 360));
      }
    }
    return { bytes: await srcDoc.save(), filename: 'rotated-document.pdf' };
  }

  throw new Error('Unknown operation');
}

processBtn.addEventListener('click', async () => {
  if (!files.length) return;
  processBtn.disabled = true;
  message('pdf-status', isAr ? 'جارٍ معالجة المستند محليًا وتنزيله…' : 'Processing document and downloading…');
  try {
    const { bytes, filename } = await buildOutputPdf();
    const blob = new Blob([bytes], { type: 'application/pdf' });
    saveBlob(blob, filename);
    message('pdf-status', isAr ? `تم تنزيل المستند بنجاح (${formatBytes(blob.size)})` : `Document downloaded successfully (${formatBytes(blob.size)})`, 'success');
    trackEvent('tool_completed', 'pdf-toolkit');
  } catch (error) {
    message('pdf-status', formatError(error, isAr), 'error');
    trackEvent('tool_failed', 'pdf-toolkit');
  } finally {
    processBtn.disabled = false;
  }
});

